import { useGetFeatureToggleVariant } from '@seek/hirer-feature-toggle';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FEATURE_TOGGLE_CART_EDIT_FLOW } from 'src/app/shared/config/features';
import { isAbortError } from 'src/app/utils/isAbortError';
import { log } from 'src/app/utils/logging';
import { actionUpdateSelectedProductId } from 'src/app/v1/state/modules/formData/selectedProductId';
import { actionUpdateCartLineItem } from 'src/app/v1/state/modules/session/cart';
import { actionUpdateCreateProductSetRequest } from 'src/app/v1/state/modules/session/createProductSetRequest';
import type { CartLineItem } from 'src/app/v1/state/types/cart';
import type { AdProductUpgrade } from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';

import { useCallbacksForCreateProductSetForAdEditing } from '../useCallbacksForCreateProductSetForAdEditing';
import { useCreateProductSet } from '../useCreateProductSet';

const useRefreshProductSetForAdEditing = (isDataLoading: boolean) => {
  const { getInputsForLogging, createProductSetMutation, isInputSame } =
    useCallbacksForCreateProductSetForAdEditing();
  const createProductSet = useCreateProductSet({
    getInputsForLogging,
    createProductSetMutation,
    isInputSame,
  });
  const dispatch = useDispatch();
  const { seekAdvertiserId, sourceJobId } = useSelector((state: AppState) => ({
    seekAdvertiserId: state.session.seekAdvertiserId,
    sourceJobId: state.session.sourceJobId,
  }));

  const isCartEnabledInEditFlow = useGetFeatureToggleVariant(
    FEATURE_TOGGLE_CART_EDIT_FLOW,
  )?.value;

  useEffect(() => {
    if (isDataLoading || isCartEnabledInEditFlow) {
      return;
    }

    const getProductSet = async () => {
      const createProductSetRequest = createProductSet();
      dispatch(actionUpdateCreateProductSetRequest(createProductSetRequest));

      const data = await createProductSetRequest;

      // only logging error here because UX wants error from publish action only
      if (data.error && !isAbortError(data.error)) {
        log.error(
          'useRefreshProductSetForAdEditing | createProductSet | Create product set failed',
          {
            error: data.error,
            ...getInputsForLogging(),
          },
        );
      }

      const jobAdEditProduct = data.productSet
        ? (data.productSet.items as Array<AdProductUpgrade>).find((item) =>
            item?.features.some((feature) => feature?.type === 'JobAdEdit'),
          )
        : undefined;

      if (data.productSet && jobAdEditProduct) {
        dispatch(actionUpdateSelectedProductId(jobAdEditProduct.id));

        const cartItem: CartLineItem = {
          type: jobAdEditProduct.__typename ?? 'AdProductUpgrade',
          productSetId: data.productSet?.id,
          jobId: sourceJobId!,
          productId: jobAdEditProduct.id,
          revisionId: '',
        };

        dispatch(actionUpdateCartLineItem(cartItem));
      }
    };

    getProductSet();
  }, [
    createProductSet,
    dispatch,
    getInputsForLogging,
    isCartEnabledInEditFlow,
    isDataLoading,
    seekAdvertiserId,
    sourceJobId,
  ]);
};

export { useRefreshProductSetForAdEditing };
